/**
 * Composer-Paragraphen-Suche: §-Parse + Labels für Lookup/Heften.
 * Kein LEXCORE-Korpus im Browser — nur UI-Erkennung; Nachschlagen über rechtsfrage.
 */

export interface ParagraphRef {
  /** z. B. "16" oder "16 Abs. 1" */
  section: string;
  /** z. B. "MRG", "ABGB" — optional wenn nur Zahl */
  law?: string;
  /** Kanonisches Label „§ 16 MRG“ */
  label: string;
  /** Stabiler Key für Listen */
  key: string;
}

/** Häufige AT-Kurzformen für Typeahead (kein Korpus). */
export const PARAGRAPH_LAW_HINTS = [
  'MRG',
  'ABGB',
  'LFG',
  'WEG',
  'KSchG',
  'ZPO',
  'EO',
  'StGB',
  'RAO',
  'UGB',
  'DSG',
  'DSGVO',
] as const;

const LAW_ALT = PARAGRAPH_LAW_HINTS.join('|');
const SECTION = String.raw`(\d{1,4}[a-z]?(?:\s*(?:Abs\.?|Absatz)\s*\d{1,2})?)`;

/** „§ 16 MRG“, „§ 24f LFG“, „LFG-24f“, „MRG § 16“ */
const REF_RE = new RegExp(
  String.raw`(?:§{1,2}\s*)?${SECTION}\s*(?:\/\s*\d{1,2})?\s*(${LAW_ALT})\b` +
    String.raw`|(${LAW_ALT})\s*§{0,2}\s*${SECTION}` +
    String.raw`|(?:§{1,2}\s*)?(${LAW_ALT})-(\d{1,4}[a-z]?)` +
    String.raw`|§{1,2}\s*${SECTION}(?!\s*(?:Abs\.?|Absatz))`,
  'gi',
);

function preprocessParagraphQuery(raw: string): string {
  return String(raw || '')
    .replace(/\bLuftfahrtgesetz\b/gi, 'LFG')
    .trim();
}

function normalizeSection(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

function makeRef(section: string, law?: string): ParagraphRef {
  const sec = normalizeSection(section);
  const lawUp = law?.trim().toUpperCase() || undefined;
  const label = lawUp ? `§ ${sec} ${lawUp}` : `§ ${sec}`;
  return {
    section: sec,
    law: lawUp,
    label,
    key: `${lawUp ?? '§'}:${sec}`.toLowerCase(),
  };
}

/** Extrahiert alle erkannten §-Refs aus freiem Tippen. */
export function parseParagraphQuery(query: string): ParagraphRef[] {
  const text = preprocessParagraphQuery(query);
  if (!text) return [];

  const found = new Map<string, ParagraphRef>();
  const re = new RegExp(REF_RE.source, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    let ref: ParagraphRef | null = null;
    if (m[1] && m[2]) ref = makeRef(m[1], m[2]);
    else if (m[3] && m[4]) ref = makeRef(m[4], m[3]);
    else if (m[5] && m[6]) ref = makeRef(m[6], m[5]);
    else if (m[7]) ref = makeRef(m[7]);
    if (ref) found.set(ref.key, ref);
  }

  // Fallback: „16 MRG“ / „24f LFG“ / „LFG-24f“
  if (found.size === 0) {
    const idForm = text.match(
      new RegExp(String.raw`^§?\s*(${LAW_ALT})-(\d{1,4}[a-z]?)\s*$`, 'i'),
    );
    if (idForm?.[1] && idForm[2]) {
      const ref = makeRef(idForm[2], idForm[1]);
      found.set(ref.key, ref);
    }
  }
  if (found.size === 0) {
    const loose = text.match(
      new RegExp(String.raw`^§?\s*(\d{1,4}[a-z]?)\s*(${LAW_ALT})?\s*$`, 'i'),
    );
    if (loose?.[1]) {
      const ref = makeRef(loose[1], loose[2]);
      found.set(ref.key, ref);
    }
  }

  // „§§ 16, 29 MRG“: Gesetz am Ende auf bare §-Nummern übertragen
  const trailingLaw = text.match(new RegExp(String.raw`\b(${LAW_ALT})\s*$`, 'i'));
  if (trailingLaw) {
    const law = trailingLaw[1].toUpperCase();
    const bareNums = text.matchAll(/§{0,2}\s*(\d{1,4}[a-z]?)/gi);
    for (const n of bareNums) {
      const ref = makeRef(n[1], law);
      found.set(ref.key, ref);
    }
    for (const [key, ref] of [...found.entries()]) {
      if (!ref.law) {
        found.delete(key);
        const fixed = makeRef(ref.section, law);
        found.set(fixed.key, fixed);
      }
    }
  }

  return Array.from(found.values());
}

/** Typeahead-Vorschläge aus Query (lokal, ohne API). */
export function suggestParagraphRefs(query: string, limit = 6): ParagraphRef[] {
  const parsed = parseParagraphQuery(query);
  if (parsed.length > 0) return parsed.slice(0, limit);

  const q = query.trim();
  if (!q) {
    return PARAGRAPH_LAW_HINTS.slice(0, 4).map((law) => makeRef('…', law));
  }

  const numOnly = q.match(/^§?\s*(\d{1,4}[a-z]?)\s*$/i);
  if (numOnly?.[1]) {
    return PARAGRAPH_LAW_HINTS.slice(0, limit).map((law) => makeRef(numOnly[1], law));
  }

  const lawOnly = q.match(new RegExp(String.raw`^(${LAW_ALT}|Luftfahrtgesetz)\s*$`, 'i'));
  if (lawOnly?.[1]) {
    const law = /luftfahrtgesetz/i.test(lawOnly[1]) ? 'LFG' : lawOnly[1].toUpperCase();
    const nums = law === 'LFG' ? ['24f', '24j', '146', '164'] : ['16', '29', '1', '12'];
    return nums.map((n) => makeRef(n, law)).slice(0, limit);
  }

  return [];
}

/** Lookup-Prompt: Nachschlagen ohne Fallanalyse. */
export function buildParagraphLookupPrompt(refs: ParagraphRef[]): string {
  const labels = refs.map((r) => r.label).join(', ');
  return (
    `Bitte nur nachschlagen: ${labels}. ` +
    'Kurzer Normwortlaut bzw. Überblick — keine Fallanalyse und kein Sachverhalt.'
  );
}

/** Kontextblock für angeheftete §§ (Composer / Webhook-Text). */
export const ATTACHED_PARAGRAPH_CONTEXT_HEAD = 'Angeheftete Paragraphen:';

export function buildAttachedParagraphContext(refs: ParagraphRef[]): string {
  if (!refs.length) return '';
  const labels = refs.map((r) => r.label).join('; ');
  return `${ATTACHED_PARAGRAPH_CONTEXT_HEAD} ${labels}\n\n`;
}

/** Pin-Prefix ist LEX-Kontext, keine eigene Rechtsfrage. */
export function stripAttachedParagraphPrefix(text: string): string {
  const raw = String(text || '');
  const idx = raw.indexOf(ATTACHED_PARAGRAPH_CONTEXT_HEAD);
  if (idx < 0) return raw;
  const after = raw.slice(idx + ATTACHED_PARAGRAPH_CONTEXT_HEAD.length);
  const split = after.indexOf('\n\n');
  const rest = split >= 0 ? after.slice(split + 2) : after.replace(/^[^\n]*\n?/, '');
  return `${raw.slice(0, idx)}${rest}`.replace(/^\s+/, '');
}

export function mergeParagraphRefs(
  existing: ParagraphRef[],
  incoming: ParagraphRef[],
): ParagraphRef[] {
  const map = new Map(existing.map((r) => [r.key, r]));
  for (const r of incoming) map.set(r.key, r);
  return Array.from(map.values());
}
